import { Link } from "react-router-dom";
import { ArrowLeft, Download } from "lucide-react";
import Logo from "@/components/Logo";

const LOCKUPS = [
  {
    n: "A.01",
    label: "Primary",
    desc: "Deep ink on warm cream",
    preview: "/brand/cosalpha-wordmark-primary.svg",
    bg: "bg-[var(--cream)]",
    files: [
      { l: "SVG", href: "/brand/cosalpha-wordmark-primary.svg" },
      { l: "PNG · 720", href: "/brand/cosalpha-wordmark-primary-720.png" },
      { l: "PNG · 1440", href: "/brand/cosalpha-wordmark-primary-1440.png" },
      { l: "PNG · 2160", href: "/brand/cosalpha-wordmark-primary-2160.png" },
    ],
  },
  {
    n: "A.02",
    label: "Reversed",
    desc: "Cream on deep ink",
    preview: "/brand/cosalpha-wordmark-reversed.svg",
    bg: "bg-[var(--ink)]",
    files: [
      { l: "SVG", href: "/brand/cosalpha-wordmark-reversed.svg" },
      { l: "PNG · 720", href: "/brand/cosalpha-wordmark-reversed-720.png" },
      { l: "PNG · 1440", href: "/brand/cosalpha-wordmark-reversed-1440.png" },
      { l: "PNG · 2160", href: "/brand/cosalpha-wordmark-reversed-2160.png" },
    ],
  },
  {
    n: "A.03",
    label: "Monochrome",
    desc: "Single-colour black, print fallback",
    preview: "/brand/cosalpha-wordmark-mono.svg",
    bg: "bg-white border border-[var(--ink)]/15",
    files: [
      { l: "SVG", href: "/brand/cosalpha-wordmark-mono.svg" },
      { l: "PNG · 720", href: "/brand/cosalpha-wordmark-mono-720.png" },
      { l: "PNG · 1440", href: "/brand/cosalpha-wordmark-mono-1440.png" },
    ],
  },
  {
    n: "A.04",
    label: "Display flourish",
    desc: "Cover & feature openers, ≥ 96pt only",
    preview: "/brand/cosalpha-wordmark-display.svg",
    bg: "bg-[var(--cream)]",
    files: [
      { l: "SVG", href: "/brand/cosalpha-wordmark-display.svg" },
      { l: "PNG · 720", href: "/brand/cosalpha-wordmark-display-720.png" },
      { l: "PNG · 1440", href: "/brand/cosalpha-wordmark-display-1440.png" },
      { l: "PNG · 2160", href: "/brand/cosalpha-wordmark-display-2160.png" },
    ],
  },
];

const PALETTE = [
  { name: "Deep ink", role: "Primary", share: "70%", hex: "#0F1E2E", rgb: "15 · 30 · 46", pms: "539 C", swatch: "bg-[var(--ink)]", text: "text-[var(--cream)]" },
  { name: "Warm cream", role: "Background", share: "20%", hex: "#F4EFE6", rgb: "244 · 239 · 230", pms: "7527 C", swatch: "bg-[var(--cream)] border border-[var(--ink)]/15", text: "text-[var(--ink)]" },
  { name: "Saffron", role: "Accent", share: "7%", hex: "#D89B2C", rgb: "216 · 155 · 44", pms: "130 C", swatch: "bg-[var(--saffron)]", text: "text-[var(--ink)]" },
  { name: "Warm grey", role: "Secondary", share: "3%", hex: "#6B6B68", rgb: "107 · 107 · 104", pms: "Cool Grey 10", swatch: "bg-[var(--warm-grey)]", text: "text-[var(--cream)]" },
];

function Lockup({ n, label, desc, preview, bg, files }) {
  return (
    <div className="border border-[var(--ink)]/15 bg-white" data-testid={`brand-card-${label.replace(/\s/g, "-")}`}>
      <div className={`h-56 flex items-center justify-center p-8 ${bg}`}>
        <img src={preview} alt={label} className="max-h-full max-w-full" />
      </div>
      <div className="p-5 border-t border-[var(--ink)]/15">
        <div className="flex items-baseline justify-between">
          <span className="font-mono text-[11px] tracking-[0.2em] text-[var(--saffron)]">{n}</span>
          <span className="smallcaps text-[var(--warm-grey)]">{label}</span>
        </div>
        <div className="font-serif text-[16px] mt-3 text-[var(--ink)]/85">{desc}</div>
        <div className="mt-5 flex flex-wrap gap-2">
          {files.map((f) => (
            <a
              key={f.href}
              href={f.href}
              download
              className="inline-flex items-center gap-1.5 text-[11px] tracking-wide px-3 py-1.5 bg-[var(--ink)] hover:bg-[var(--ink)]/85 text-[var(--cream)] transition-colors"
              data-testid={`brand-download-${f.href}`}
            >
              <Download className="w-3 h-3" /> {f.l}
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Brand() {
  return (
    <div className="min-h-screen paper text-[var(--ink)]" data-testid="brand-page">
      <header className="border-b border-[var(--ink)]/15 bg-[var(--cream)]/85 backdrop-blur-xl">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12 py-4 flex items-center justify-between">
          <Link to="/"><Logo size={22} /></Link>
          <Link to="/" className="text-[12px] tracking-[0.18em] uppercase font-semibold text-[var(--warm-grey)] hover:text-[var(--ink)] inline-flex items-center gap-2 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to site
          </Link>
        </div>
      </header>
      <main className="max-w-[1200px] mx-auto px-6 lg:px-12 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="smallcaps text-[var(--saffron)]">Media kit · Vol. 01 / 2026</div>
            <h1 className="font-serif text-[60px] font-medium tracking-[-0.025em] mt-5 leading-[1]">A wordmark, set in a serif.</h1>
            <hr className="saffron-rule mt-8" />
            <p className="text-[16px] leading-[1.7] text-[var(--ink)]/85 mt-8 max-w-[58ch]">
              The name carries the mark. cosalpha is set in a high-quality editorial serif with tight
              tracking; the alpha sits inside the word, not beside it. There is no symbol, no monogram,
              no glyph.
            </p>
          </div>
          <aside className="lg:col-span-5 lg:pl-8 lg:border-l border-[var(--ink)]/15">
            <div className="smallcaps text-[var(--warm-grey)]">Don't</div>
            <ul className="mt-4 space-y-2 text-[13px] text-[var(--ink)]/80 list-disc ml-5">
              <li>Distort, stretch or condense the wordmark</li>
              <li>Recolour outside the approved palette</li>
              <li>Rotate, skew or set on a path</li>
              <li>Pair with a glyph, monogram or icon lockup</li>
              <li>Apply drop shadows or other effects</li>
            </ul>
          </aside>
        </div>

        <section className="mt-20">
          <div className="smallcaps text-[var(--saffron)]">No. 01 — The mark</div>
          <h2 className="font-serif text-[34px] font-medium tracking-[-0.02em] mt-3 mb-8">Lockups.</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {LOCKUPS.map((m) => <Lockup key={m.n} {...m} />)}
          </div>
        </section>

        <section className="mt-20">
          <div className="smallcaps text-[var(--saffron)]">No. 02 — Palette</div>
          <h2 className="font-serif text-[34px] font-medium tracking-[-0.02em] mt-3 mb-8">Five colours, used in proportion.</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {PALETTE.map((c) => (
              <div key={c.hex} className="border border-[var(--ink)]/15 bg-white">
                <div className={`h-44 flex items-end p-5 ${c.swatch} ${c.text}`}>
                  <div>
                    <div className="font-mono text-[10px] tracking-[0.2em] uppercase opacity-70">{c.share}</div>
                    <div className="font-serif text-[24px] mt-1">{c.name}</div>
                  </div>
                </div>
                <div className="p-5 border-t border-[var(--ink)]/15 text-[12px] tabular space-y-1">
                  <div><span className="smallcaps text-[var(--warm-grey)] mr-2">Role</span>{c.role}</div>
                  <div className="font-mono">HEX  {c.hex}</div>
                  <div className="font-mono">RGB  {c.rgb}</div>
                  <div className="font-mono">PMS  {c.pms}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-20 border border-[var(--ink)]/15 bg-white p-10">
          <div className="smallcaps text-[var(--saffron)]">No. 03 — Typography</div>
          <h2 className="font-serif text-[34px] font-medium tracking-[-0.02em] mt-3">An editorial serif, a quiet grotesque.</h2>
          <hr className="saffron-rule mt-6 mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <div className="smallcaps text-[var(--warm-grey)]">Display & body</div>
              <p className="font-serif text-[60px] mt-4 leading-[1] tracking-[-0.025em]">Source Serif 4</p>
              <p className="text-[13px] mt-4 text-[var(--ink)]/75 leading-[1.7]">Used for display, headlines, long-form, and the wordmark itself. Generous line-height; small caps for labels.</p>
            </div>
            <div>
              <div className="smallcaps text-[var(--warm-grey)]">UI & captions</div>
              <p className="font-sans text-[60px] mt-4 leading-[1] tracking-[-0.02em] font-medium">Inter</p>
              <p className="text-[13px] mt-4 text-[var(--ink)]/75 leading-[1.7]">Used for UI, navigation, captions, tabular figures. All numbers set in tabular style.</p>
            </div>
          </div>
        </section>

        <section className="mt-20 border-t border-[var(--ink)]/15 pt-10 text-[11px] tracking-[0.18em] uppercase font-semibold text-[var(--warm-grey)] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <span>cosalpha · brand assets · vol. 01 / 01</span>
          <span>Compiled in Ahmedabad · 2026</span>
        </section>
      </main>
    </div>
  );
}
